import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Lock, Mail, Eye, EyeOff, Monitor, ArrowLeft, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import DemoUsersPanel from '@/components/DemoUsersPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LoginPage() {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotResult, setForgotResult] = useState(null);
  const [forgotLoading, setForgotLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed');
    }
    setLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) { setError('Enter your email'); return; }
    setForgotLoading(true); setError('');
    try {
      const res = await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      setForgotResult(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not reset password');
    }
    setForgotLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#09090b] relative overflow-hidden" data-testid="login-page">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-blue-500/5 rounded-full blur-3xl pointer-events-none" />

      <div className="w-full max-w-lg mx-4 z-10 animate-fade-in">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-blue-600 rounded-sm flex items-center justify-center">
              <Monitor className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-3xl font-heading font-bold tracking-tight text-white">VawworldOS</h1>
          </div>
          <p className="text-zinc-500 text-sm font-medium tracking-wide uppercase">Vawworld Operating System</p>
        </div>

        <div className="bg-zinc-900/80 border border-zinc-800 rounded-sm p-6 backdrop-blur-sm">
          {!showForgot ? (
            /* LOGIN FORM */
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-zinc-400 mb-1.5 uppercase tracking-wider">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <input data-testid="login-email-input" type="email" value={email} onChange={e => setEmail(e.target.value)}
                    className="w-full h-9 pl-10 pr-3 bg-zinc-950 border border-zinc-800 rounded-sm text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="your@email.com" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-zinc-400 mb-1.5 uppercase tracking-wider">Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <input data-testid="login-password-input" type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
                    className="w-full h-9 pl-10 pr-10 bg-zinc-950 border border-zinc-800 rounded-sm text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="Enter password" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              {error && <p className="text-red-400 text-xs" data-testid="login-error">{error}</p>}
              <button data-testid="login-submit-button" type="submit" disabled={loading}
                className="w-full h-9 bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium rounded-sm transition-all disabled:opacity-50 glow-blue">
                {loading ? 'Authenticating...' : 'Sign In'}
              </button>
              <button type="button" onClick={() => { setShowForgot(true); setError(''); setForgotResult(null); }}
                className="w-full text-center text-[11px] text-zinc-500 hover:text-blue-400 transition-colors mt-1">
                Forgot your password?
              </button>
            </form>
          ) : (
            /* FORGOT PASSWORD FORM */
            <div className="space-y-4">
              <button onClick={() => { setShowForgot(false); setError(''); setForgotResult(null); }}
                className="flex items-center gap-1 text-xs text-zinc-500 hover:text-white transition-colors mb-2">
                <ArrowLeft className="w-3.5 h-3.5" /> Back to login
              </button>

              {forgotResult ? (
                <div className="text-center py-4">
                  <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
                  <p className="text-sm text-white font-medium">Password Reset</p>
                  <p className="text-xs text-zinc-400 mt-2">Your temporary password:</p>
                  <div className="bg-zinc-950 border border-zinc-700 rounded-sm px-4 py-3 mt-2">
                    <p className="text-lg font-mono text-emerald-400 font-bold select-all">{forgotResult.temp_password}</p>
                  </div>
                  <p className="text-[10px] text-zinc-600 mt-2">Login with this password and change it from your Profile.</p>
                  <button onClick={() => { setShowForgot(false); setPassword(forgotResult.temp_password); setEmail(forgotResult.email); setForgotResult(null); }}
                    className="mt-4 h-8 px-5 bg-blue-600 hover:bg-blue-500 text-white text-xs rounded-sm">
                    Go to Login
                  </button>
                </div>
              ) : (
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <p className="text-xs text-zinc-400">Enter your registered email to reset your password.</p>
                  <div>
                    <label className="block text-xs font-medium text-zinc-400 mb-1.5 uppercase tracking-wider">Email</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                      <input type="email" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)}
                        className="w-full h-9 pl-10 pr-3 bg-zinc-950 border border-zinc-800 rounded-sm text-sm text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                        placeholder="your@email.com" />
                    </div>
                  </div>
                  {error && <p className="text-red-400 text-xs">{error}</p>}
                  <button type="submit" disabled={forgotLoading}
                    className="w-full h-9 bg-amber-600 hover:bg-amber-500 text-white text-sm font-medium rounded-sm transition-all disabled:opacity-50">
                    {forgotLoading ? 'Resetting...' : 'Reset Password'}
                  </button>
                </form>
              )}
            </div>
          )}
        </div>

        <div className="text-center mt-5 space-y-2">
          <p className="text-[10px] text-zinc-600">New here? Register as</p>
          <div className="flex justify-center gap-3">
            <a href="/vendor/register" className="text-[11px] px-4 py-1.5 bg-violet-500/10 border border-violet-500/20 text-violet-400 rounded-sm hover:bg-violet-500/20 transition-all">Vendor / Studio</a>
            <a href="/freelancer/register" className="text-[11px] px-4 py-1.5 bg-amber-500/10 border border-amber-500/20 text-amber-400 rounded-sm hover:bg-amber-500/20 transition-all">Freelancer</a>
          </div>
          <p className="text-[9px] text-zinc-800 mt-2">Vawworld Animation Studio Production OS</p>
        </div>

        {/* Founder ask Feb 2026: preview-only one-click login switcher
            so the founder can hop between roles (admin / PM / client /
            employee / vendor L1/L2/L3) without copy-pasting passwords
            from /app/memory/test_credentials.md every time. The panel
            self-hides on vawworld.in (production). */}
        <DemoUsersPanel />
      </div>

      {/* Founder ask May 17, 2026: footer download link so artists can hit
          vawworld.in on their workstation and grab the VOS Studio Agent
          installer without needing to log in first. */}
      <div className="absolute bottom-4 left-0 right-0 flex justify-center pointer-events-none">
        <a
          href="/agent-downloads"
          data-testid="footer-agent-downloads"
          className="pointer-events-auto inline-flex items-center gap-2 text-[11px] text-zinc-600 hover:text-emerald-300 transition-colors px-3 py-1.5 rounded-md hover:bg-zinc-900/60 border border-transparent hover:border-zinc-800"
          title="Install VOS Studio Agent on this workstation"
        >
          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="3" width="20" height="14" rx="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" /></svg>
          Download VOS Studio Agent <span className="opacity-50">· Windows / Mac / Linux</span>
        </a>
      </div>
    </div>
  );
}
